import React from 'react';

import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { useConfigurationStore } from 'store/configurationsStore';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ProjectLevelChangeCard } from 'components/Dashboards/Project/Components/VersionCompare/ProjectLevelChangeCard/projectLevelChangeCard';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { LoaderContainer, WidgetCard } from 'components/common';
import { useChangeSummaryLogic } from 'components/widgets/ChangeSummary/ChangeSummary.logic';
import classes from 'components/widgets/ChangeSummary/ChangeSummary.module.scss';
import { useChangeSummaryProvider } from 'components/widgets/ChangeSummary/ChangeSummary.provider';
import { WidgetCardGraph } from 'components/widgets/ChangeSummary/components/WidgetCardGraph/WidgetCardGraph';

export const ChangeSummary = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title: propsTitle,
    setIsPayloadReady,
}: IComponentProps) => {
    const { componentKey, routeId: id, endpoints, name: configTitle } = useConfigurationStore().changeSummary;

    const { ui, api } = useChangeSummaryProvider({
        widgetId,
        globalFilterData,
        localFilters,
        compareVersionId,
        latestVersionId,
        projectId,
        contractId,
        endpoints,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        title: propsTitle ?? configTitle,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.getSummaryData.payload,
        widgetEndpoint: api.getSummaryData.endpoint,
    });

    const { logic } = useChangeSummaryLogic({ api, setIsPayloadReady, widgetViewMode });

    return (
        <WidgetWithTitle
            title={<WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />}
            className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
            id={id}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddToReport
                    key={'addWidgetToReport'}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    projectId={projectId}
                    contractId={contractId}
                    uiState={ui.state}
                    shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                    apiPayload={api.getSummaryData.payload}
                    widgetEndpoint={api.getSummaryData.endpoint}
                />,
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ExportDropdown key={'export'} title={propsTitle ?? configTitle} subTitle={subTitle} />
                ),
                <TooltipIcon key={'info'} tooltip={api.getSummaryData.instance.data?.info} />,
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ThreeDotsMenu
                        widgetViewMode={widgetViewMode}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        shareTitle={propsTitle ?? configTitle}
                        shareLink={route}
                    />
                ),
            ]}
            titleFilters={[
                <ReportingVersionAndPills
                    key={'ReportingVersionAndPills'}
                    selectedProjectAndContract={widgetSelectedProjectAndContract}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
                <Narrative key={'narrative'} editNarrative={editNarrative} />,
                <PinboardPills
                    key={'pinboardPills'}
                    isInPinboard={WidgetViewModeUtils.isPinboardView(widgetViewMode)}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
            ]}
        >
            {api.getSummaryData.instance.isFetching && !api.getSummaryData.instance.data && (
                <LoaderContainer>
                    <OverlayWithSpinner />
                </LoaderContainer>
            )}
            {!logic.isSummaryData && !api.getSummaryData.instance.data && !api.getSummaryData.instance.isFetching && (
                <NoData />
            )}
            {api.getSummaryData.instance.data && (
                <div className={classes.widgetCardsWrapper}>
                    <ProjectLevelChangeCard data={logic.projectLevelData} />
                    <ProjectLevelChangeCard data={logic.activityLevelData} />
                    {logic.graphData?.cardTrends?.length > 0 && (
                        <WidgetCard
                            contentHeight={'100%'}
                            showToggleRadioInput={true}
                            titleText={'Change'}
                            selectToggle={logic.selectToggle}
                            toggle={logic.toggle}
                        >
                            <WidgetCardGraph
                                data={logic.graphData?.cardTrends[logic.cardTrendsIndex].graphPoints}
                                xaxisTitle={'Version'}
                                yaxisTitle={logic.toggle}
                                yAxisMaxValue={logic.maxValue}
                            />
                        </WidgetCard>
                    )}
                </div>
            )}
        </WidgetWithTitle>
    );
};
